import { Flex, Group, Modal, Stack, Text, useMantineTheme } from '@mantine/core'
import { useGetDevicesList } from 'api/devices/getDevices'
import { QueryPaginated, SearchFilters } from 'components/common'
import { DeviceTypeBadge } from 'components/devices'
import {
  Children,
  Dispatch,
  ReactElement,
  ReactNode,
  SetStateAction,
  cloneElement,
  isValidElement,
  useMemo,
  useState,
  type CSSProperties,
} from 'react'
import { useTranslation } from 'react-i18next'
import { TDevice, TDeviceList } from 'shared/types'
import classes from './AssignModalList.module.css'

export type AssignModalProps = {
  assignedIds: string[]
  opened: boolean
  setOpened: Dispatch<SetStateAction<boolean>>
  children: ReactNode
}

export const AssignDeviceModal = ({
  assignedIds,
  opened,
  setOpened,
  children,
}: AssignModalProps) => {
  const { t } = useTranslation()
  const query = useGetDevicesList()

  return (
    <Modal
      size="lg"
      opened={opened}
      onClose={() => setOpened(prev => !prev)}
      title={t('sensor.assignDevice')}
    >
      <Stack gap={0}>
        <SearchFilters noWrapper groupStyle={{ marginTop: '16px' }} />
        <QueryPaginated.Wrapper query={query}>
          <QueryPaginated.Data>
            {(data: TDeviceList) =>
              data.items.map(item => (
                <ListItem
                  key={item.deviceId}
                  {...item}
                  assignedIds={assignedIds}
                >
                  {children}
                </ListItem>
              ))
            }
          </QueryPaginated.Data>
        </QueryPaginated.Wrapper>
      </Stack>
    </Modal>
  )
}

type ListItemProps = {
  children: ReactNode
} & TDevice &
  Pick<AssignModalProps, 'assignedIds'>

const ListItem = ({ assignedIds, children, ...item }: ListItemProps) => {
  const theme = useMantineTheme()
  const [isAssigned, setIsAssigned] = useState<boolean>(
    () => !!assignedIds.find(id => id === item.deviceId),
  )
  const listItemStyle = {
    '--assign-list-border-color':
      theme.colorScheme === 'dark'
        ? theme.colors.dark[4]
        : theme.colors.gray[5],
  } as CSSProperties

  const [assignButton] = useMemo(
    () => Children.toArray(children),
    [children],
  ) as ReactElement<AssignDeviceButtonProps>[]

  return (
    <Group
      align="center"
      py={'lg'}
      className={classes.item}
      style={listItemStyle}
      justify="space-between"
    >
      <Stack gap={'xs'} align="flex-start">
        <Text size="sm">{item.name}</Text>
        <Flex gap="sm" wrap="wrap">
          <DeviceTypeBadge type={item.type} />
          <Text size="sm" c="dimmed">
            {item.provider}
          </Text>
        </Flex>
      </Stack>
      {isValidElement(assignButton) &&
        cloneElement(assignButton, {
          ...assignButton.props,
          isAssigned,
          setIsAssigned,
          deviceId: item.deviceId,
          'data-testid': `assign-device-${item.name}`,
        })}
    </Group>
  )
}

export type AssignDeviceButtonProps = {
  isAssigned?: boolean
  setIsAssigned?: Dispatch<SetStateAction<boolean>>
  deviceId?: string
  onSuccess?: () => void
  ['data-testid']?: string
}
